import { z } from "zod";

/**
 * 前后端共用的契约。金额一律整数分；尺寸一律 mm；坐标归一化 0..1。
 */

export const calcMethodCodeSchema = z.enum(["area_factor", "percent_of_slots", "count_price"]);
export type CalcMethodCode = z.infer<typeof calcMethodCodeSchema>;

export const categorySchema = z.object({
  id: z.string(),
  parent_id: z.string().nullable(),
  code: z.string(),
  name: z.string(),
  name_en: z.string().nullable(),
  default_calc_method: calcMethodCodeSchema.nullable(),
  default_unit: z.string().nullable(),
  sort_order: z.number().int(),
  active: z.number().int(),
});
export type Category = z.infer<typeof categorySchema>;

export const createCategorySchema = z.object({
  parent_id: z.string().nullable().optional(),
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(64),
  name_en: z.string().max(64).nullable().optional(),
  default_calc_method: calcMethodCodeSchema.nullable().optional(),
  default_unit: z.string().max(24).nullable().optional(),
  sort_order: z.number().int().optional(),
});

export const catalogItemSchema = z.object({
  id: z.string(),
  category_id: z.string(),
  code: z.string(),
  name: z.string(),
  name_en: z.string().nullable(),
  calc_method: z.string(),
  unit: z.string(),
  unit_price_cents: z.number().int(),
  currency: z.string(),
  params: z.string(),
  tags: z.string(),
  spec: z.string().nullable(),
  active: z.number().int(),
  sort_order: z.number().int(),
});
export type CatalogItem = z.infer<typeof catalogItemSchema>;

export const createCatalogItemSchema = z.object({
  category_id: z.string().min(1),
  /** 归属部门（品牌）：不传 = 记到创建者所在部门；admin 可以指定 */
  department_id: z.string().min(1).nullable().optional(),
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(120),
  name_en: z.string().max(120).nullable().optional(),
  calc_method: calcMethodCodeSchema,
  unit: z.string().min(1).max(24),
  unit_price_cents: z.number().int().min(0),
  currency: z.string().min(1).max(8).default("CNY"),
  params: z.record(z.string(), z.unknown()).default({}),
  tags: z.array(z.string()).default([]),
  spec: z.string().max(240).nullable().optional(),
  sort_order: z.number().int().optional(),
});

export const updateCatalogItemSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  /** 英文名：英文客户报价单上的材料名；清空传 null 就退回中文名 */
  name_en: z.string().max(120).nullable().optional(),
  calc_method: calcMethodCodeSchema.optional(),
  unit: z.string().min(1).max(24).optional(),
  unit_price_cents: z.number().int().min(0).optional(),
  params: z.record(z.string(), z.unknown()).optional(),
  tags: z.array(z.string()).optional(),
  spec: z.string().max(240).nullable().optional(),
  active: z.union([z.literal(0), z.literal(1)]).optional(),
  sort_order: z.number().int().optional(),
});

export const cabinetTypeSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  name_en: z.string().nullable(),
  slots: z.string(),
  /** 默认高/深（mm）：画框时预填，用户可改；null = 不预填 */
  default_height_mm: z.number().int().nullable(),
  default_depth_mm: z.number().int().nullable(),
  active: z.number().int(),
  sort_order: z.number().int(),
});
export type CabinetType = z.infer<typeof cabinetTypeSchema>;

export const cabinetSlotSchema = z.object({
  slot: z.string().min(1),
  categoryCode: z.string().min(1),
  calcMethod: calcMethodCodeSchema,
  params: z.record(z.string(), z.unknown()).default({}),
  defaultCatalogCode: z.string().nullable().optional(),
  required: z.boolean().default(false),
});
export type CabinetSlot = z.infer<typeof cabinetSlotSchema>;

/**
 * 槽位 ↔ 顶层分类。柜体槽位只能装柜体类组件，门板槽位只能装门板类组件……
 * 两边都靠它判断"这个材料能不能放进这个槽位"（前端限制候选、服务端兜底拒绝）。
 */
export const SLOT_BY_CATEGORY: Record<string, string> = {
  cabinet: "box",
  door: "door",
  panel: "panel",
  countertop: "countertop",
  hardware: "hardware",
  accessory: "accessory",
};

/** 分类可能带二级路径（cabinet.plywood），比对上只用顶层 */
export function topCategoryCode(categoryCode: string): string {
  return categoryCode.split(".")[0] ?? "";
}

/**
 * HTTP 请求头只能放 ISO-8859-1 字符。中文文件名（"户型图.pdf"）直接塞进 `x-file-name`
 * 会让浏览器在发请求前就抛错：Failed to execute 'fetch' … String contains non ISO-8859-1 code point。
 * 所以文件名一律先 percent-encode（UTF-8），并带上 x-file-name-encoding: pct 标记。
 */
export const FILE_NAME_ENCODING_HEADER = "x-file-name-encoding";
export const FILE_NAME_ENCODING_PCT = "pct";

export function encodeHeaderFileName(fileName: string): string {
  return encodeURIComponent(fileName);
}

/**
 * 解回头文件名。只有客户端声明了 pct 才解，老客户端（纯 ASCII 名字、没标记）原样使用；
 * 万一解不开也原样返回——宁可名字里多个 %，也不能让上传失败。
 */
export function decodeHeaderFileName(value: string, encoding?: string | null): string {
  if (encoding !== FILE_NAME_ENCODING_PCT) return value;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * 换材料时这一行该用哪些参数。
 *
 * 口径（factor / percent / countFrom / basisSlots…）用**新组件**的：换成 Blum 就该按 15% 算。
 * 但**手填的量**必须跟着旧行走，不然换一次材质就等于把用户填过的数抹掉：
 * - `fixedCount`：抽屉/导轨/拉篮这类按件项的数量（"6 个抽屉" 不能变成 1 个）；
 * - `manualAreaM2`：见光板/台面手量的面积；
 * - `requiresManualArea`：新组件没声明时沿用旧行的。
 */
export function mergeReplaceParams(
  oldParams: Record<string, unknown>,
  newParams: Record<string, unknown>,
): Record<string, unknown> {
  const merged = { ...newParams };
  if (oldParams.fixedCount !== undefined) merged.fixedCount = oldParams.fixedCount;
  if (oldParams.manualAreaM2 !== undefined) merged.manualAreaM2 = oldParams.manualAreaM2;
  if (merged.requiresManualArea === undefined && oldParams.requiresManualArea !== undefined) {
    merged.requiresManualArea = oldParams.requiresManualArea;
  }
  return merged;
}

/** 组件的顶层分类 → 它该待在哪个槽位；认不出来返回 null（例如自定义扩展槽位） */
export function slotForCategoryCode(categoryCode: string): string | null {
  return SLOT_BY_CATEGORY[topCategoryCode(categoryCode)] ?? null;
}

export const planSchema = z.object({
  id: z.string(),
  parent_id: z.string().nullable(),
  department_id: z.string(),
  name: z.string(),
  code: z.string().nullable(),
  assignee_id: z.string().nullable(),
  cost_currency: z.string(),
  currency: z.string(),
  exchange_rate: z.number(),
  markup_factor: z.number(),
  global_adjustment_percent: z.number(),
  /** 付款方式（每份方案自选） */
  payment_terms: z.string(),
  /** 整装出货：按货值加收 7% 打包组装费 */
  assembled_shipment: z.number().int(),
  status: z.string(),
  total_cost_cents: z.number().int(),
  total_price_cents: z.number().int(),
  public_token: z.string().nullable(),
  share_scope: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  sort_order: z.number().int(),
});
export type Plan = z.infer<typeof planSchema>;

export const createPlanSchema = z.object({
  name: z.string().min(1).max(120),
  parent_id: z.string().nullable().optional(),
  department_id: z.string().min(1),
  code: z.string().max(64).nullable().optional(),
  assignee_id: z.string().nullable().optional(),
  exchange_rate: z.number().positive().optional(),
  markup_factor: z.number().positive().optional(),
  global_adjustment_percent: z.number().optional(),
});

/** 客户报价的销售币种（人民币 / 澳元 / 美元 / 英镑 / 欧元） */
export const salesCurrencySchema = z.enum(["USD", "AUD", "GBP", "EUR", "CNY"]);
export type SalesCurrencyCode = z.infer<typeof salesCurrencySchema>;

/**
 * 付款方式：每份方案选一种，报价单上照它出条款。
 * 加新的一种 = 这里加一个 code + 前端补两版文案，不动引擎。
 */
export const PAYMENT_TERMS_CODES = ["deposit_50_50", "deposit_5000_70"] as const;
export const paymentTermsSchema = z.enum(PAYMENT_TERMS_CODES);
export type PaymentTermsCode = z.infer<typeof paymentTermsSchema>;

/** 整装出货的打包组装费：按货值（条目报价合计）的百分比加收，报价单上单列一行。 */
export const ASSEMBLY_SURCHARGE_PERCENT = 7;

export const updatePlanSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  /** 销售币种：改它时会按默认汇率重算，汇率也可以一起传 */
  currency: salesCurrencySchema.optional(),
  exchange_rate: z.number().positive().max(1000).optional(),
  markup_factor: z.number().positive().max(100).optional(),
  global_adjustment_percent: z.number().min(-100).max(500).optional(),
  /** 报价单是否附平面图（效果图不适合进报价单时可以关掉） */
  include_floor_plan: z.boolean().optional(),
  /** 付款方式：每份方案自己选，报价单上按这个出条款 */
  payment_terms: paymentTermsSchema.optional(),
  /** 整装出货：开了就按货值加收 7% 打包组装费（报价单上单列一行） */
  assembled_shipment: z.boolean().optional(),
});

/**
 * 一键复制方案：可以顺手指定副本名字；不传就自动叫「xx（副本）」。
 * 具体拷什么由服务端定（图纸登记 + 标尺 + 条目 + 组件快照 + 子方案），前端不需要知道。
 */
export const duplicatePlanSchema = z.object({
  name: z.string().min(1).max(120).optional(),
});

/** 批量替换：把某个槽位（柜体/门板/台面/五金/配件…）的材料整单或按柜型换掉 */
export const bulkReplaceSchema = z.object({
  scope: z.union([
    z.object({ type: z.literal("plan") }),
    z.object({ type: z.literal("cabinet_type"), code: z.string().min(1) }),
    z.object({ type: z.literal("items"), itemIds: z.array(z.string()).min(1) }),
  ]),
  slot: z.string().min(1).max(32),
  catalog_item_id: z.string().min(1),
  /** 只替换"当前用的是这个组件"的行；不传 = 该槽位所有行都换成新组件 */
  from_catalog_code: z.string().min(1).optional(),
});

/** 批量加项：把按件项目（纸箱 / 木箱 / 组装…）加到整单 / 某柜型 / 指定条目的每组柜子上 */
export const bulkAddSchema = z.object({
  scope: z.union([
    z.object({ type: z.literal("plan") }),
    z.object({ type: z.literal("cabinet_type"), code: z.string().min(1) }),
    z.object({ type: z.literal("items"), itemIds: z.array(z.string()).min(1) }),
  ]),
  slot: z.string().min(1).max(32),
  catalog_item_id: z.string().min(1),
  /** 每组柜子加几个（默认 1，写进该行的 params.fixedCount） */
  quantity_per_item: z.number().min(0).max(999).optional(),
  /** 默认 true：已经有这个项目的柜体跳过，避免重复点两次变成两份 */
  skip_existing: z.boolean().optional(),
});

export const planItemSchema = z.object({
  id: z.string(),
  plan_id: z.string(),
  drawing_id: z.string().nullable(),
  page_index: z.number().int(),
  box_x: z.number(),
  box_y: z.number(),
  box_w: z.number(),
  box_h: z.number(),
  cabinet_type_code: z.string(),
  label: z.string(),
  room: z.string().nullable(),
  note: z.string().nullable(),
  width_mm: z.number().nullable(),
  height_mm: z.number().nullable(),
  depth_mm: z.number().nullable(),
  doors: z.number().int(),
  drawers: z.number().int(),
  shelves: z.number().int(),
  quantity: z.number().int(),
  attributes: z.string(),
  size_status: z.string(),
  cost_cents: z.number().int(),
  price_cents: z.number().int(),
  sort_order: z.number().int(),
});
export type PlanItem = z.infer<typeof planItemSchema>;

export const createPlanItemSchema = z.object({
  drawing_id: z.string().nullable().optional(),
  page_index: z.number().int().min(0).default(0),
  box_x: z.number().min(0).max(1),
  box_y: z.number().min(0).max(1),
  box_w: z.number().gt(0).max(1),
  box_h: z.number().gt(0).max(1),
  cabinet_type_code: z.string().min(1).default("base"),
  label: z.string().max(64).optional(),
  room: z.string().max(64).nullable().optional(),
  width_mm: z.number().int().positive().nullable().optional(),
  height_mm: z.number().int().positive().nullable().optional(),
  depth_mm: z.number().int().positive().nullable().optional(),
  doors: z.number().int().min(0).optional(),
  drawers: z.number().int().min(0).optional(),
  shelves: z.number().int().min(0).optional(),
  quantity: z.number().int().positive().optional(),
});

/**
 * PATCH 语义：**只改传进来的字段**。
 *
 * 不能写成 `createPlanItemSchema.partial()`——zod v4 的 `.partial()` 只把字段包成 optional，
 * 字段自己的 `.default()` 还在，于是"只发坐标"的请求会被补出 `page_index: 0` 和
 * `cabinet_type_code: "base"`，服务端照单全收：在多页图纸上拖一下框，条目就悄悄回到第 1 页，
 * 柜型也被打回地柜。所以这里手写一份不带默认值的更新契约。
 */
export const updatePlanItemSchema = z.object({
  drawing_id: z.string().nullable().optional(),
  page_index: z.number().int().min(0).optional(),
  box_x: z.number().min(0).max(1).optional(),
  box_y: z.number().min(0).max(1).optional(),
  box_w: z.number().gt(0).max(1).optional(),
  box_h: z.number().gt(0).max(1).optional(),
  cabinet_type_code: z.string().min(1).optional(),
  label: z.string().max(64).optional(),
  room: z.string().max(64).nullable().optional(),
  width_mm: z.number().int().positive().nullable().optional(),
  height_mm: z.number().int().positive().nullable().optional(),
  depth_mm: z.number().int().positive().nullable().optional(),
  doors: z.number().int().min(0).optional(),
  drawers: z.number().int().min(0).optional(),
  shelves: z.number().int().min(0).optional(),
  quantity: z.number().int().positive().optional(),
  rotation_deg: z.number().min(-360).max(360).optional(),
  attributes: z.record(z.string(), z.unknown()).optional(),
  note: z.string().max(500).nullable().optional(),
});

/**
 * 条目排序：把拖拽后的**整份顺序**一次提交（数组下标即新次序）。
 *
 * 只改 `plan_item.sort_order`，**不改 `label`**（编号是报价单里的对应关系，必须稳定）。
 * 允许少传（并发新增的条目会按原相对顺序排到后面），不允许重复、不属于本方案的 id。
 */
export const reorderPlanItemsSchema = z.object({
  item_ids: z.array(z.string().min(1)).min(1).max(500),
});

/**
 * 新条目的编号（A01 / A02 …）怎么取：**现有 A 编号的最大值 +1**，而不是"当前条数 +1"。
 *
 * 回归背景：条数是"删了就少一个"的量。删掉 A02 之后条数回到 2，下一个新建的条目又叫 A03，
 * 跟还活着的 A03 撞号——图纸、报价单、工厂核价表上同时出现两个 A03，对应关系就断了。
 * 按最大值递增还能保证编号不回退、已删编号不回收（历史报价单里的 A03 指的还是当时那个柜体）。
 * 人工指定过编号（data.label）的条目以人工值为准，不走这里。
 */
export function nextPlanItemLabel(existingLabels: string[]): string {
  // 大小写不敏感：人工把编号敲成小写 a03，也当作已占用
  const taken = new Set(existingLabels.map((label) => label.trim().toLowerCase()));
  let maxNumber = 0;
  for (const label of taken) {
    const match = /^a(\d+)$/.exec(label);
    if (match) maxNumber = Math.max(maxNumber, Number(match[1]));
  }
  let next = maxNumber + 1;
  // 兜底：万一这个编号已被别的写法占用，继续往后找
  while (taken.has(`a${String(next).padStart(2, "0")}`)) next += 1;
  return `A${String(next).padStart(2, "0")}`;
}

export const planItemComponentSchema = z.object({
  id: z.string(),
  item_id: z.string(),
  slot: z.string(),
  category_id: z.string().nullable(),
  catalog_item_id: z.string().nullable(),
  catalog_code: z.string(),
  name_snapshot: z.string(),
  calc_method: z.string(),
  params: z.string(),
  unit: z.string(),
  unit_price_cents: z.number().int(),
  quantity: z.number(),
  amount_cents: z.number().int(),
  state: z.string(),
  engine_version: z.string(),
  computed_at: z.string().nullable(),
  notes: z.string().nullable(),
  sort_order: z.number().int(),
});
export type PlanItemComponent = z.infer<typeof planItemComponentSchema>;

export const addItemComponentSchema = z.object({
  slot: z.string().min(1).max(32),
  catalog_item_id: z.string().min(1),
  params: z.record(z.string(), z.unknown()).optional(),
  sort_order: z.number().int().optional(),
});

export const updateItemComponentSchema = z.object({
  params: z.record(z.string(), z.unknown()).optional(),
  catalog_item_id: z.string().min(1).optional(),
  sort_order: z.number().int().optional(),
});

export const createDrawingSchema = z.object({
  file_name: z.string().min(1).max(200),
  sha256: z.string().length(64),
  page_count: z.number().int().min(0).default(0),
});

export const reportDrawingPagesSchema = z.object({
  pages: z
    .array(
      z.object({
        page_index: z.number().int().min(0),
        width: z.number().positive(),
        height: z.number().positive(),
        rotation: z.number().int().default(0),
      }),
    )
    .min(1),
});

export const generateQuoteSchema = z.object({
  /** 只补缺失的 slot（默认），或整体重套柜型模板。 */
  mode: z.enum(["fill_missing", "reset_all"]).default("fill_missing"),
  cabinet_type_code: z.string().optional(),
});

export type ApiError = { error: { code: string; message: string; details?: unknown } };

export const memberRoleSchema = z.enum(["admin", "leader", "employee"]);
export type MemberRole = z.infer<typeof memberRoleSchema>;

export const createDepartmentSchema = z.object({
  name: z.string().min(1).max(48),
  code: z
    .string()
    .min(1)
    .max(32)
    .regex(/^[a-z0-9._-]+$/)
    .optional(),
  status: z.enum(["active", "disabled"]).default("active"),
});

export const updateDepartmentSchema = z.object({
  name: z.string().min(1).max(48).optional(),
  status: z.enum(["active", "disabled"]).optional(),
});

export const createMemberSchema = z.object({
  department_id: z.string().min(1),
  email: z.string().email().max(160),
  display_name: z.string().max(48).nullable().optional(),
  role: memberRoleSchema.default("employee"),
});

export const updateMemberSchema = z.object({
  department_id: z.string().min(1).optional(),
  display_name: z.string().max(48).nullable().optional(),
  role: memberRoleSchema.optional(),
  status: z.enum(["active", "suspended"]).optional(),
});

export const healthSchema = z.object({
  ok: z.boolean(),
  environment: z.string(),
  calcMethods: z.array(z.string()),
  now: z.string(),
});

export const loginSchema = z.object({
  email: z.string().email().max(160),
  password: z.string().min(1).max(200),
});

export const changePasswordSchema = z.object({
  current_password: z.string().min(1).max(200),
  new_password: z.string().min(8).max(200),
});

export const setMemberPasswordSchema = z.object({
  password: z.string().min(8).max(200),
});

/** 标尺：图上归一化线段 + 它代表的真实长度（mm） */
export const rulerSchema = z.object({
  x1: z.number().min(0).max(1),
  y1: z.number().min(0).max(1),
  x2: z.number().min(0).max(1),
  y2: z.number().min(0).max(1),
  lengthMm: z.number().positive().max(1_000_000),
});
